import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { User, Building2, Bell, CreditCard, SlidersHorizontal, Shield, Link2, Globe, Coins } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { LanguageSwitcher } from '@/components/organisms/header/language-switcher';
import { CurrencySwitcher } from '@/components/organisms/header/currency-switcher';

export default function SettingsPage() {
  const { t } = useTranslation();

  const settingsSections = [
    {
      title: t('settings.profile.title'),
      description: t('settings.profile.personalInfoDescription'),
      icon: User,
      href: '/settings/profile',
    },
    {
      title: t('settings.company.title'),
      description: t('settings.company.readOnlyNotice'),
      icon: Building2,
      href: '/settings/company',
    },
    {
      title: t('settings.notifications.title'),
      description: t('settings.notifications.description'),
      icon: Bell,
      href: '/settings/notifications',
    },
    {
      title: t('settings.subscription.title'),
      description: t('settings.subscription.choosePlan'),
      icon: CreditCard,
      href: '/settings/subscription',
    },
    {
      title: t('settings.preferences.title'),
      description: t('settings.preferences.description'),
      icon: SlidersHorizontal,
      href: '/settings/preferences',
    },
    {
      title: t('settings.security'),
      description: t('settings.profile.securityDescription'),
      icon: Shield,
      href: '/settings/profile/password',
    },
    {
      title: t('associations.title'),
      description: t('associations.settingsDescription'),
      icon: Link2,
      href: '/settings/associations',
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">{t('settings.title')}</h1>

      {/* Quick Settings - Mobile only */}
      <Card className="sm:hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('settings.quickSettings')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="size-5 text-muted-foreground" />
              <span className="text-sm">{t('settings.language')}</span>
            </div>
            <LanguageSwitcher />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Coins className="size-5 text-muted-foreground" />
              <span className="text-sm">{t('settings.currency')}</span>
            </div>
            <CurrencySwitcher />
          </div>
          <Link to="/settings/notifications" className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <Bell className="size-5 text-muted-foreground" />
              <span className="text-sm">{t('settings.notifications.title')}</span>
            </div>
            <span className="text-xs text-muted-foreground">→</span>
          </Link>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {settingsSections.map((section) => (
          <Link key={section.href} to={section.href}>
            <Card className="h-full hover:border-primary/50 transition-colors cursor-pointer">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-muted p-2">
                    <section.icon className="size-5 text-muted-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{section.title}</CardTitle>
                    <CardDescription className="text-sm line-clamp-2">
                      {section.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
